import type { ReactNode } from "react";

import type {
  OrganizationSupportEvent,
  OrganizationTool,
} from "../../shared/organization-overview-schemas";
import { Badge } from "../components/ui/badge";

function Section({
  children,
  description,
  title,
}: {
  children: ReactNode;
  description: string;
  title: string;
}) {
  return (
    <section aria-label={title} className="space-y-2">
      <h4 className="text-lg font-bold">{title}</h4>
      <p className="text-muted-foreground">{description}</p>
      {children}
    </section>
  );
}

function ScrollTable({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="overflow-x-auto">
      <table aria-label={label} className="w-full min-w-md border-collapse text-left">
        {children}
      </table>
    </div>
  );
}

function formatDate(value: number | null): string {
  return value === null ? "—" : new Date(value * 1000).toISOString().slice(0, 10);
}

const HEADER = "border-b border-muted text-sm text-muted-foreground";

export function DeploymentsSection({ tools }: { tools: OrganizationTool[] }) {
  const deployed = tools.filter((tool) => tool.activeDeployment !== null);
  return (
    <Section
      description="The version each tool is serving now. Customers never see these identifiers."
      title="Deployments"
    >
      {deployed.length === 0 ? (
        <p className="text-muted-foreground">No tool has an active deployment recorded.</p>
      ) : (
        <ScrollTable label="Deployments">
          <thead>
            <tr className={HEADER}>
              <th className="py-2 pr-4 font-semibold">Tool</th>
              <th className="py-2 pr-4 font-semibold">Version</th>
              <th className="py-2 pr-4 font-semibold">Template</th>
              <th className="py-2 pr-4 font-semibold">Deployed</th>
              <th className="py-2 font-semibold">State</th>
            </tr>
          </thead>
          <tbody>
            {deployed.map((tool) => (
              <tr className="border-b border-muted last:border-b-0" key={tool.id}>
                <td className="py-2 pr-4 font-semibold">{tool.displayName}</td>
                <td className="py-2 pr-4 font-mono text-xs">
                  {tool.activeDeployment?.versionId ?? "—"}
                </td>
                <td className="py-2 pr-4">{tool.activeDeployment?.templateVersion}</td>
                <td className="py-2 pr-4">
                  {formatDate(tool.activeDeployment?.deployedAt ?? null)}
                </td>
                <td className="py-2">
                  <Badge
                    variant={
                      tool.activeDeployment?.status === "deployed" ? "success" : "destructive"
                    }
                    className="capitalize"
                  >
                    {tool.activeDeployment?.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      )}
    </Section>
  );
}

export function SupportHistorySection({
  events,
  total,
}: {
  events: OrganizationSupportEvent[] | null;
  total: number | null;
}) {
  return (
    <Section
      description="Every expiring support session an employee opened for this customer."
      title={total === null ? "Support history" : `Support history (${total})`}
    >
      {events === null ? (
        <p className="text-muted-foreground">
          Only employees who may start support access for this customer see this history.
        </p>
      ) : events.length === 0 ? (
        <p className="text-muted-foreground">
          No employee has opened support access for this customer.
        </p>
      ) : (
        <ScrollTable label="Support history">
          <thead>
            <tr className={HEADER}>
              <th className="py-2 pr-4 font-semibold">Employee</th>
              <th className="py-2 pr-4 font-semibold">Person</th>
              <th className="py-2 pr-4 font-semibold">Could change things</th>
              <th className="py-2 pr-4 font-semibold">Started</th>
              <th className="py-2 font-semibold">State</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr className="border-b border-muted last:border-b-0" key={event.id}>
                <td className="py-2 pr-4">
                  <span className="font-semibold">{event.employeeDisplayName}</span>
                  <span className="block text-sm text-muted-foreground">{event.reason}</span>
                </td>
                <td className="py-2 pr-4">{event.targetUserDisplayName ?? "Whole workspace"}</td>
                <td className="py-2 pr-4">{event.mode === "write" ? "Yes" : "No, view only"}</td>
                <td className="py-2 pr-4">{formatDate(event.createdAt)}</td>
                <td className="py-2 capitalize">{event.state}</td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      )}
    </Section>
  );
}
